
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetSevaCategoriesQuery, useGetSevasByCategoryQuery } from '../redux/api/sevaApi';

/* ------------------------------------------------------------------ */
/* Sub-component: renders seva rows for a single donation sub-category */
/* ------------------------------------------------------------------ */
const DonationCategoryRows = ({ category, cartItems, onToggleSeva }) => {
  const { data: sevasData, isLoading, isError } = useGetSevasByCategoryQuery(category._id);

  if (isLoading) return (
    <tr>
      <td colSpan="3" className="text-center py-2">Loading...</td>
    </tr>
  );
  if (isError) return (
    <tr>
      <td colSpan="3" className="text-danger py-2">Failed to load sevas</td>
    </tr>
  );
  if (!sevasData || !sevasData.data || sevasData.data.length === 0) return null;

  return (
    <>
      {sevasData.data.map((seva) => {
        const isChecked = cartItems.some(item => item._id === seva._id);
        return (
          <tr
            key={seva._id}
            onClick={() => onToggleSeva(seva, category.category_name)}
            style={{ cursor: 'pointer', backgroundColor: isChecked ? '#fff5f5' : 'transparent' }}
          >
            {/* Seva Name */}
            <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {category.image_path
                  ? <img src={category.image_path} alt="" style={{ width: '28px', height: '28px', objectFit: 'cover', borderRadius: '50%' }} />
                  : <i className="fa-solid fa-hand-holding-heart" style={{ color: '#c20000', fontSize: '1.1rem' }}></i>
                }
                <div>
                  <div style={{ fontWeight: '600', color: '#222', fontSize: '0.95rem' }}>{seva.seva_name}</div>
                  <div style={{ fontSize: '0.78rem', color: '#888' }}>{category.category_name}</div>
                </div>
              </div>
            </td>

            {/* Amount from master */}
            <td style={{ padding: '12px 14px', fontWeight: 'bold', color: '#c20000', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
              ₹{seva.seva_amount}
            </td>

            {/* Checkbox */}
            <td style={{ padding: '12px 14px', textAlign: 'center', verticalAlign: 'middle' }}>
              <input
                className="style2"
                type="checkbox"
                id={`donation-${seva._id}`}
                checked={isChecked}
                onChange={() => onToggleSeva(seva, category.category_name)}
                onClick={(e) => e.stopPropagation()}
              />
            </td>
          </tr>
        );
      })}
    </>
  );
};

/* ------------------------------------------------------------------ */
/* Main Page                                                           */
/* ------------------------------------------------------------------ */
const BookingDonation = () => {
  const navigate = useNavigate();
  const { data: categoriesData, isLoading: isCatLoading, isError, error } = useGetSevaCategoriesQuery();

  /* ---- Date ---- */
  const [selectedDate, setSelectedDate] = useState(() => {
    return sessionStorage.getItem('selected_seva_date') || new Date().toISOString().split('T')[0];
  });

  useEffect(() => {
    sessionStorage.setItem('selected_seva_date', selectedDate);
  }, [selectedDate]);

  const todayStr = new Date().toISOString().split('T')[0];
  const maxDate = new Date();
  maxDate.setMonth(maxDate.getMonth() + 3);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  /* ---- Cart ---- */
  const [cartItems, setCartItems] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem('cart_donation')) || [];
    } catch (e) {
      return [];
    }
  });

  useEffect(() => {
    sessionStorage.setItem('cart_donation', JSON.stringify(cartItems));
  }, [cartItems]);

  /* ---- Custom (manual) donation entry ---- */
  const [customCause, setCustomCause] = useState('');
  const [customAmount, setCustomAmount] = useState('');

  /* Toggle check/uncheck — amount comes from master, no manual entry */
  const handleToggleSeva = (seva, categoryName) => {
    setCartItems((prev) => {
      const exists = prev.some(item => item._id === seva._id);
      if (exists) {
        return prev.filter(item => item._id !== seva._id);
      } else {
        return [...prev, { ...seva, god_name: categoryName }];
      }
    });
  };

  /* Filtered donation categories */
  const donationCategories = categoriesData?.data?.filter(
    c => c.master_category_id && c.master_category_id.name === 'Donations'
  ) || [];

  const customAmountNum = parseInt(customAmount, 10);
  const hasValidCustom = !isNaN(customAmountNum) && customAmountNum > 0;

  /* Total = checkbox sevas + custom amount (if valid) */
  const totalAmount =
    cartItems.reduce((acc, item) => acc + Number(item.seva_amount), 0) +
    (hasValidCustom ? customAmountNum : 0);

  /* Build full cart payload for summary page */
  const buildFullCart = () => {
    const items = [...cartItems];
    if (hasValidCustom) {
      items.push({
        _id: 'custom_donation',
        seva_name: customCause.trim() || 'Custom Donation',
        seva_amount: customAmountNum,
        god_name: 'Other',
        is_custom: true,
      });
    }
    return items;
  };

  const hasAnything = cartItems.length > 0 || hasValidCustom;

  return (
    <>
      <div className="space-extra-bottom space-top">
        <div className="container">

          {/* Page Title */}
          <div className="title-area text-center mb-5">
            <h2 className="sec-title" style={{ color: '#c20000' }}>DONATIONS</h2>
            <img src="assets/img/theme-img/title_shape2.svg" alt="" style={{ maxWidth: '200px' }} />
          </div>

          {/* Date picker */}
          <div className="row justify-content-center mb-4">
            <div className="col-lg-5 col-md-7">
              <div className="p-3 bg-white rounded shadow-sm" style={{ border: '1px solid #ddd' }}>
                <label className="mb-2 d-block" style={{ fontWeight: '600', color: '#c20000' }}>
                  <i className="fa-regular fa-calendar-days me-2"></i>
                  Donation date:
                </label>
                <input
                  type="date"
                  className="form-control text-center"
                  value={selectedDate}
                  min={todayStr}
                  max={maxDateStr}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  style={{ borderRadius: '8px', border: '1px solid #c20000', fontWeight: 'bold' }}
                />
                <small className="text-muted mt-1 d-block">* Date can be selected up to 3 months in advance.</small>
              </div>
            </div>
          </div>

          {/* Seva list table */}
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="bg-white rounded shadow-sm" style={{ borderTop: '4px solid #c20000', overflow: 'hidden' }}>

                {isCatLoading && <div className="text-center p-4">Loading Donations...</div>}
                {isError && (
                  <div className="text-center p-4 text-danger">
                    Error: {error?.data?.message || error?.message || 'Unknown error'}
                  </div>
                )}

                {!isCatLoading && donationCategories.length > 0 && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', color: '#000' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#c20000', color: '#fff' }}>
                        <th style={{ padding: '12px 14px', fontWeight: '600', fontSize: '0.95rem' }}>Seva / Donation Name</th>
                        <th style={{ padding: '12px 14px', fontWeight: '600', fontSize: '0.95rem' }}>Amount</th>
                        <th style={{ padding: '12px 14px', fontWeight: '600', fontSize: '0.95rem', textAlign: 'center' }}>Select</th>
                      </tr>
                    </thead>
                    <tbody>
                      {donationCategories.map(category => (
                        <DonationCategoryRows
                          key={category._id}
                          category={category}
                          cartItems={cartItems}
                          onToggleSeva={handleToggleSeva}
                        />
                      ))}
                    </tbody>
                  </table>
                )}

                {!isCatLoading && donationCategories.length === 0 && !isError && (
                  <div className="text-center p-4 text-muted">No donation sevas available.</div>
                )}
              </div>
            </div>
          </div>

          {/* ---- Custom / Manual Donation Section ---- */}
          <div className="row justify-content-center mt-4">
            <div className="col-lg-10">
              <div
                className="bg-white rounded shadow-sm p-4"
                style={{ borderTop: '4px solid #b8860b' }}
              >
                {/* Instructional label */}
                <p style={{
                  fontSize: '0.92rem',
                  color: '#555',
                  marginBottom: '18px',
                  lineHeight: '1.6',
                  borderLeft: '4px solid #b8860b',
                  paddingLeft: '12px',
                  fontStyle: 'italic'
                }}>
                  <i className="fa-solid fa-circle-info me-2" style={{ color: '#b8860b' }}></i>
                  If you wish to contribute a different amount towards any of the above, please mention the cause and your desired donation amount.
                </p>

                <div className="row g-3">
                  {/* Cause / Reason input */}
                  <div className="col-md-7">
                    <label style={{ fontWeight: '600', color: '#333', fontSize: '0.9rem', marginBottom: '6px', display: 'block' }}>
                      Cause / Reason
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Temple renovation, Anna Danam, etc."
                      value={customCause}
                      onChange={(e) => setCustomCause(e.target.value)}
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #ccc',
                        padding: '10px 14px',
                        fontSize: '0.95rem'
                      }}
                    />
                  </div>

                  {/* Amount input */}
                  <div className="col-md-5">
                    <label style={{ fontWeight: '600', color: '#333', fontSize: '0.9rem', marginBottom: '6px', display: 'block' }}>
                      Donation Amount (₹)
                    </label>
                    <div style={{ position: 'relative' }}>
                      <span style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontWeight: 'bold',
                        color: '#c20000',
                        fontSize: '1rem',
                        pointerEvents: 'none'
                      }}>₹</span>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Enter amount"
                        min="1"
                        value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        style={{
                          borderRadius: '8px',
                          border: '1px solid #ccc',
                          padding: '10px 14px 10px 28px',
                          fontSize: '0.95rem'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Live preview chip */}
                {hasValidCustom && (
                  <div className="mt-3 d-flex align-items-center gap-2">
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      backgroundColor: '#fff3cd',
                      border: '1px solid #b8860b',
                      borderRadius: '20px',
                      padding: '4px 14px',
                      fontSize: '0.88rem',
                      color: '#7a5900',
                      fontWeight: '600'
                    }}>
                      <i className="fa-solid fa-check-circle"></i>
                      {customCause.trim() || 'Custom Donation'} — ₹{customAmountNum}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Fixed footer when items selected OR valid custom amount entered */}
          {hasAnything && (
            <div style={{
              position: 'fixed',
              bottom: '0',
              left: '0',
              width: '100%',
              backgroundColor: '#7a0000',
              padding: '15px 30px',
              boxShadow: '0 -4px 15px rgba(0,0,0,0.3)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              zIndex: 1000
            }}>
              <div className="container d-flex justify-content-between align-items-center">
                <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#ffffff' }}>
                  Total:&nbsp;
                  <span style={{ color: '#FFD700', fontWeight: '800', fontSize: '1.6rem' }}>
                    ₹{totalAmount}/-
                  </span>
                </div>
                <button
                  className="th-btn"
                  onClick={() => navigate('/seva-summary', { state: { cartItems: buildFullCart(), sevaDate: selectedDate, isDonation: true } })}
                  style={{ padding: '12px 40px', fontSize: '1rem' }}
                >
                  Proceed
                </button>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Padding so last row not hidden behind fixed footer */}
      {hasAnything && <div style={{ height: '100px' }} />}
    </>
  );
};

export default BookingDonation;
